import { createFileRoute, type ParsedLocation, redirect } from "@tanstack/react-router";
import { z } from "zod";

import {
  captureAuthError,
  trackLoginFailed,
  trackLoginSucceeded,
} from "@/lib/auth/auth-tracking";
import {
  logOAuthCallbackError,
  logOAuthCallbackFinished,
  logOAuthCallbackNoExternalId,
  logOAuthCallbackStarted,
} from "@/lib/breadcrumbs";
import { stytchClient } from "@/lib/stytch";
import { redirectToReturnToOrFallback } from "@/lib/utils/redirect-to-return-to";
import type { RootRouteContext } from "@/routes/-root/-root-context";

const SESSION_DURATION_MINUTES = 60 * 24 * 30; // 30 days

const oauthCallbackSearch = z.object({
  token: z.string().optional(),
  stytch_token_type: z.string().optional(),
});

type OAuthCallbackSearch = z.infer<typeof oauthCallbackSearch>;

const hasTrustedMetadata = (
  trustedMetadata: Record<string, unknown> | undefined,
): boolean => {
  return Object.keys(trustedMetadata ?? {}).length > 0;
};

/**
 * The OAuth callback URL carries the Stytch token in its query string, and the
 * breadcrumb loggers record `location.href`. Strip the query string (keeping
 * only the pathname) before any logging so the token never reaches Sentry.
 */
const sanitizeLocation = (location: ParsedLocation): ParsedLocation => ({
  ...location,
  href: location.pathname,
  searchStr: "",
});

const validateOAuthParams = (
  search: OAuthCallbackSearch,
): search is Required<OAuthCallbackSearch> => {
  const { token, stytch_token_type: stytchTokenType } = search;

  return Boolean(token) && stytchTokenType === "oauth";
};

interface AuthenticateOAuthTokenResult {
  success: boolean;
  hasExternalId: boolean;
  shouldDeleteEmailBeforeLogout: boolean;
  emailIds: string[];
}

const authenticateOAuthToken = async (
  location: ParsedLocation,
  token: string,
): Promise<AuthenticateOAuthTokenResult> => {
  try {
    const response = await stytchClient.oauth.authenticate(token, {
      session_duration_minutes: SESSION_DURATION_MINUTES,
    });

    const hasExternalId = Boolean(response.user.external_id);

    return {
      success: true,
      hasExternalId,
      shouldDeleteEmailBeforeLogout:
        !hasExternalId && !hasTrustedMetadata(response.user.trusted_metadata),
      emailIds: response.user.emails.map(({ email_id: emailId }) => emailId),
    };
  } catch (error) {
    logOAuthCallbackError({ location, error });
    trackLoginFailed("social", error);
    captureAuthError(error, { flow: "login", method: "social" });

    return {
      success: false,
      hasExternalId: false,
      shouldDeleteEmailBeforeLogout: false,
      emailIds: [],
    };
  }
};

const deleteUserEmails = async (
  location: ParsedLocation,
  emailIds: string[],
): Promise<void> => {
  for (const emailId of emailIds) {
    try {
      await stytchClient.user.deleteEmail(emailId);
    } catch (error) {
      logOAuthCallbackError({ location, error });
    }
  }
};

const revokeSessionAndRedirect = async (
  context: RootRouteContext,
  location: ParsedLocation,
  options?: {
    shouldDeleteEmailBeforeLogout: boolean;
    emailIds: string[];
  },
): Promise<void> => {
  if (options?.shouldDeleteEmailBeforeLogout) {
    await deleteUserEmails(location, options.emailIds);
  }

  try {
    await context.stytchAuthenticationClient.logout();
  } catch (error) {
    logOAuthCallbackError({ location, error });
  }

  logOAuthCallbackNoExternalId({ location });

  redirect({ to: "/auth/first-access/document", throw: true });
};

const beforeLoad = async (
  context: RootRouteContext,
  location: ParsedLocation,
  search: OAuthCallbackSearch,
): Promise<void> => {
  const safeLocation = sanitizeLocation(location);

  logOAuthCallbackStarted({ location: safeLocation });

  if (!validateOAuthParams(search)) {
    const error = new Error("Missing or invalid OAuth token");

    logOAuthCallbackError({ location: safeLocation, error });
    trackLoginFailed("social", error);
    captureAuthError(error, { flow: "login", method: "social" });

    redirect({ to: "/auth/signin", throw: true, search: { action: "error" } });
    return;
  }

  const result = await authenticateOAuthToken(safeLocation, search.token);

  if (!result.success) {
    redirect({ to: "/auth/first-access/document", throw: true });
    return;
  }

  if (!result.hasExternalId) {
    await revokeSessionAndRedirect(context, safeLocation, {
      shouldDeleteEmailBeforeLogout: result.shouldDeleteEmailBeforeLogout,
      emailIds: result.emailIds,
    });
    return;
  }

  trackLoginSucceeded("social");
  logOAuthCallbackFinished({ location: safeLocation });

  redirectToReturnToOrFallback("/");
};

export const Route = createFileRoute("/auth/oauth-callback")({
  validateSearch: oauthCallbackSearch,
  async beforeLoad({ context, location, search }) {
    await beforeLoad(context, location, search);
  },
});
